import {Card, ProgressBar} from 'platform/components';
import {Box, HStack, Heading, Icon, Text, VStack} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {OperationTypeCount} from '@dms/api/settings';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {
  DEFAULT_OPERATION_ICON,
  OPERATION_ICON_BACKGROUND,
  OPERATION_ICON_COLOR,
  PAY_PER_USE_OPERATION_ICON,
} from '../constants/PAY_PER_USE_OPERATION_ICON';
import {calculateDelta} from '../utils/calculateDelta';
import {calculatePercentageOfTotal} from '../utils/calculatePercentageOfTotal';
import {getOperationGroupId} from '../utils/getOperationGroupId';
import {getOperationTypeLabel} from '../utils/getOperationTypeLabel';
import {PayPerUseTrend} from './PayPerUseTrend';

const PERCENT_FACTOR = 100;
const ICON_SIZE = 4;

interface PayPerUseOperationCardProps extends RequiredTestIdProps {
  operationType: OperationTypeCount;
  totalCount: number;
}

export function PayPerUseOperationCard(props: PayPerUseOperationCardProps) {
  const formatNumber = useFormatNumber();

  const groupId = getOperationGroupId(props.operationType);
  const icon = PAY_PER_USE_OPERATION_ICON[groupId] ?? DEFAULT_OPERATION_ICON;

  const title = match(getOperationTypeLabel(props.operationType))
    .with({kind: 'translation'}, (label) => i18n.t(label.translationKey))
    .with({kind: 'raw'}, (label) => label.text)
    .exhaustive();

  const delta = calculateDelta(props.operationType.count, props.operationType.previousCount);
  const shareOfTotal =
    calculatePercentageOfTotal(props.operationType.count, props.totalCount) / PERCENT_FACTOR;

  return (
    <Card data-testid={suffixTestId('card', props)}>
      <VStack spacing={3}>
        <HStack spacing={2} align="center">
          <Box backgroundColor={OPERATION_ICON_BACKGROUND} borderRadius="small" padding={2}>
            <Icon value={icon} color={OPERATION_ICON_COLOR} size={ICON_SIZE} />
          </Box>
          <Text size="small" alternative>
            {title}
          </Text>
        </HStack>

        <HStack justify="space-between" align="center">
          <Heading size={2}>{formatNumber(props.operationType.count)}</Heading>
          <PayPerUseTrend delta={delta} data-testid={suffixTestId('card-trend', props)} />
        </HStack>

        <ProgressBar
          percentage={shareOfTotal}
          small
          data-testid={suffixTestId('card-share', props)}
        />
      </VStack>
    </Card>
  );
}
